import React, { useEffect, useRef, useState } from 'react'
import Layout from '../../components/Layout/Layout'
import useImageUpload from '../../hooks/useImageUpload';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import { showToastNotification } from '../../utility/helper';
import InputField from '../../components/UI/Forms/InputField';
import ErrorMessage from '../../components/UI/Notifications/ErrorMessage';
import Button from '../../components/UI/Buttons/Button';
import Textarea from '../../components/UI/Forms/Textarea';
import SelectDropdown from '../../components/UI/Forms/SelectDropdown';
import { useNavigate, useParams } from 'react-router-dom';
import { IRecipe } from '../../interfaces/recipes';
import useFileDelete from '../../hooks/useFileDelete';
import SeoForm, { SeoFormRef, submitSEOData } from '../../components/common/SEOForm';
import { SeoFormData } from '../../interfaces/seo';

const AddRecipe: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { uploadImage, error } = useImageUpload();
    const { deleteFile } = useFileDelete();
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors }, reset, setValue, control } = useForm({ mode: 'onBlur' });
    const { fields: ingredients, append: appendIngredient, remove: removeIngredient } = useFieldArray({
        control,
        name: 'ingredient',
    });
    const { fields: instructions, append: appendInstruction, remove: removeInstruction } = useFieldArray({
        control,
        name: 'instruction',
    });
    const [categories] = useState<{ value: string, label: string }[]>([
        { value: 'Breakfast', label: 'Breakfast' },
        { value: 'Lunch', label: 'Lunch' },
        { value: 'Dinner', label: 'Dinner' },
        { value: 'Snack', label: 'Snack' },
        { value: 'Dessert', label: 'Dessert' },
    ]);
    const [searchQuery, setSearchQuery] = useState('');
    const [products, setProducts] = useState<{ value: string, label: string }[]>([]);
    const [imageURLs, setImageURLs] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [isSlugTouched, setIsSlugTouched] = useState(false);
    const seoFormRef = useRef<SeoFormRef>(null);
    const [seoData, setSeoData] = useState<SeoFormData>({
        entityType: 'recipe',
    });
    const titleValue = useWatch({
        control,
        name: 'title',
    });


    useEffect(() => {
        if (titleValue && !isSlugTouched) {
            const generatedSlug = generateSlug(titleValue);
            setValue('slug', generatedSlug);
        }
    }, [titleValue, setValue, control, isSlugTouched]);

    const generateSlug = (text: string) => {
        return text
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };


    const handleSlugFocus = () => {
        setIsSlugTouched(true);
    };

    useEffect(() => {
        if (id) {
            fetchRecipe();
        }
    }, [id]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchQuery) {
                fetchProducts(searchQuery)
            } else {
                setProducts([])
            }
        }, 300); // Delay of 300ms

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery]);

    const fetchProducts = async (query: string) => {
        try {
            const response = await callApi(API_URL_2 + API_ENDPOINT.page + `products?title=${query}`, { method: 'GET' });
            const newProducts = response.data.map((prod: any) => ({ value: prod.id, label: prod.title }));
            setProducts(newProducts);
        } catch (error: any) {
            console.error('Failed to fetch products:', error.message);
        }
    };

    const fetchProductDetails = async (productId: string) => {
        try {
            const product = await callApi(API_URL_2 + API_ENDPOINT.page + `products/${productId}`, { method: 'GET' });
            setValue('productName', product.title);
        } catch (error: any) {
            console.error('Failed to fetch product details:', error.message);
        }
    };


    const fetchRecipe = async () => {
        try {
            const response: IRecipe = await callApi(`${API_URL_2 + API_ENDPOINT.page}recipes/${id}`, { method: 'GET' });
            reset(response);
            if (response.slug) {
                setIsSlugTouched(true);
            }
            if (response.seo) {
                setSeoData(response.seo);
            }
            fetchProductDetails(response.productId)
            setImageURLs(response.images || []);
        } catch (error: any) {
            showToastNotification('danger', error.message);
        }
    };

    const onSubmit = async (data: any) => {
        setLoading(true);
        try {
            const url = id ? `${API_URL_2 + API_ENDPOINT.page}recipes/${id}` : `${API_URL_2 + API_ENDPOINT.page}recipes`;
            const method = id ? 'PUT' : 'POST';
            const response = await callApi(url, { method, data });
            if (seoFormRef.current) {
                const isValid = await seoFormRef.current.triggerValidation();
                const seoFormData = seoFormRef.current.getFormData();
                if (isValid) {
                    let newSeoData = {
                        ...seoFormData,
                        entityId: response.id,
                        entityType: 'recipe',
                    }
                    submitSEOData(newSeoData, seoData.id);
                } else {
                    showToastNotification('danger', 'SEO form has validation errors');
                    // You can focus on the first error or show a message
                }
            }
            showToastNotification('success', `Recipe ${id ? 'updated' : 'created'} successfully`);
            reset();
            navigate('/recipes');
        } catch (error: any) {
            showToastNotification('danger', error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) return;

        const uploadedURLs: string[] = [];
        for (const file of Array.from(files)) {
            try {
                const uploadedUrl = await uploadImage(file);
                uploadedURLs.push(uploadedUrl);
            } catch (err) {
                console.error(err);
            }
        }

        setImageURLs((prev) => [...prev, ...uploadedURLs]);
        setValue('images', [...imageURLs, ...uploadedURLs]);
    };

    const handleDeleteImage = async (url: string) => {
        setImageURLs((prev) => prev.filter((image) => image !== url));
        setValue('images', imageURLs.filter((image) => image !== url));
        await deleteFile(url);
    };


    return (
        <Layout>
            <h1 className="app-page-title">Add Recipe</h1>
            <div className="row">
                <div className="col-lg-8 col-12">
                    <div className='app-card app-card-orders-table shadow-sm mb-4 p-4'>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="row">
                                {/* Title */}
                                <div className="mb-3 col-12">
                                    <InputField
                                        type="text"
                                        register={register('title', { required: 'Title is required' })}
                                        name="title"
                                        label="Title"
                                        placeholder="Enter title"
                                    />
                                    <ErrorMessage message={errors.title?.message?.toString()} />
                                </div>

                                <div className='mb-3  col-12'>
                                    <label htmlFor="slug" className="form-label">Slug</label>
                                    <input
                                        type="text"
                                        {...register('slug', {
                                            required: 'Slug is required',
                                            pattern: {
                                                value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                                                message: 'Slug must be URL-friendly: lowercase letters, numbers, hyphens. No spaces or special characters.'
                                            }
                                        })}
                                        className='form-control'
                                        name="slug"
                                        placeholder="Enter slug"
                                        onFocus={handleSlugFocus}
                                        onChange={() => setIsSlugTouched(true)}
                                    />
                                    <ErrorMessage message={errors.slug?.message?.toString()} />
                                </div>


                                {/* Description */}
                                <div className="mb-3 col-12">
                                    <Textarea
                                        register={register('description', { required: 'Description is required' })}
                                        name="description"
                                        label="Description"
                                        placeholder="Enter description"
                                    />
                                    <ErrorMessage message={errors.description?.message?.toString()} />
                                </div>

                                {/* Is Recommended */}
                                <div className="mb-3 col-12">
                                    <div className='d-flex gap-1 '>
                                        <input
                                            className='form-check-input'
                                            type="checkbox"
                                            {...register('isRecommended')}
                                        />
                                        <label className='form-check-label'>
                                            Is Recommended
                                        </label>

                                    </div>
                                </div>

                                {/* Meal Type */}
                                <div className="mb-3 col-12">
                                    <SelectDropdown
                                        register={register('mealType', { required: 'Type of Meal is required' })}
                                        name="mealType"
                                        label="Type of Meal"
                                        options={categories}
                                    />
                                    <ErrorMessage message={errors.mealType?.message?.toString()} />
                                </div>

                                <div className="mb-3 col-12">
                                    <InputField
                                        type="text"
                                        name="search"
                                        label="Product"
                                        placeholder="Type to search..."
                                        register={register('productName', { required: 'Product is required', onChange: (e) => setSearchQuery(e.target.value) })}
                                    />
                                    <div className="product-dropdown">
                                        {products.map((product) => (
                                            <div
                                                key={product.value}
                                                className="product-item"
                                                onClick={() => {
                                                    setValue("productName", product.label);
                                                    setValue("productId", product.value);
                                                    setProducts([])
                                                    setSearchQuery('');
                                                }}
                                            >
                                                {product.label}
                                            </div>
                                        ))}
                                    </div>
                                    <ErrorMessage message={errors.productName?.message?.toString()} />
                                </div>


                                {/* Ingredients */}
                                <div className="mb-3 col-12">
                                    <div className='mb-3'>
                                        <div className='d-flex justify-content-between flex-wrap align-items-center mb-3 mt-4'>
                                            <h5 className='fs-5 fw-bold '>Ingredients</h5>
                                            <button type="button" className='btn app-btn-primary d-flex align-items-center gap-2' onClick={() => appendIngredient({ name: '' })}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M6 0C6.13261 0 6.25979 0.0526785 6.35355 0.146447C6.44732 0.240215 6.5 0.367392 6.5 0.5V5.5H11.5C11.6326 5.5 11.7598 5.55268 11.8536 5.64645C11.9473 5.74021 12 5.86739 12 6C12 6.13261 11.9473 6.25979 11.8536 6.35355C11.7598 6.44732 11.6326 6.5 11.5 6.5H6.5V11.5C6.5 11.6326 6.44732 11.7598 6.35355 11.8536C6.25979 11.9473 6.13261 12 6 12C5.86739 12 5.74021 11.9473 5.64645 11.8536C5.55268 11.7598 5.5 11.6326 5.5 11.5V6.5H0.5C0.367392 6.5 0.240215 6.44732 0.146447 6.35355C0.0526785 6.25979 0 6.13261 0 6C0 5.86739 0.0526785 5.74021 0.146447 5.64645C0.240215 5.55268 0.367392 5.5 0.5 5.5H5.5V0.5C5.5 0.367392 5.55268 0.240215 5.64645 0.146447C5.74021 0.0526785 5.86739 0 6 0Z" fill="white"></path></svg>                                        Add Ingredient
                                            </button>
                                        </div>

                                        {ingredients.map((field, index) => (
                                            <div key={field.id} className="ingredient-row mb-4">
                                                <div className="row">
                                                    <div className="col-12">
                                                        <div className='mb-2'>
                                                            <InputField
                                                                type="text"
                                                                register={register(`ingredient.${index}.name`, { required: 'Ingredient name is required' })}
                                                                name={`ingredient.${index}.name`}
                                                                label='Ingredient name'
                                                                placeholder="Ingredient name"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className='col-12'><button className='btn btn-danger' type="button" onClick={() => removeIngredient(index)}>Remove</button></div>
                                                </div>

                                            </div>
                                        ))}
                                    </div>

                                </div>
                                {/* instructionHeading */}
                                <div className="mb-3 col-12">
                                    <Textarea
                                        register={register('instructionHeading', { required: 'Instructions overview is required' })}
                                        name="instructionHeading"
                                        label="Instructions Overview"
                                        placeholder="Enter Instructions Overview"
                                    />
                                    <ErrorMessage message={errors.instructionHeading?.message?.toString()} />
                                </div>
                                {/* Instructions */}
                                <div className="mb-3 col-12">
                                    <div className='mb-3'>
                                        <div className='d-flex justify-content-between flex-wrap align-items-center mb-3 mt-4'>
                                            <h5 className='fs-5 fw-bold'>Instructions</h5>
                                            <button type="button" className='btn app-btn-primary d-flex align-items-center gap-2' onClick={() => appendInstruction({ step: '', description: '' })}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M6 0C6.13261 0 6.25979 0.0526785 6.35355 0.146447C6.44732 0.240215 6.5 0.367392 6.5 0.5V5.5H11.5C11.6326 5.5 11.7598 5.55268 11.8536 5.64645C11.9473 5.74021 12 5.86739 12 6C12 6.13261 11.9473 6.25979 11.8536 6.35355C11.7598 6.44732 11.6326 6.5 11.5 6.5H6.5V11.5C6.5 11.6326 6.44732 11.7598 6.35355 11.8536C6.25979 11.9473 6.13261 12 6 12C5.86739 12 5.74021 11.9473 5.64645 11.8536C5.55268 11.7598 5.5 11.6326 5.5 11.5V6.5H0.5C0.367392 6.5 0.240215 6.44732 0.146447 6.35355C0.0526785 6.25979 0 6.13261 0 6C0 5.86739 0.0526785 5.74021 0.146447 5.64645C0.240215 5.55268 0.367392 5.5 0.5 5.5H5.5V0.5C5.5 0.367392 5.55268 0.240215 5.64645 0.146447C5.74021 0.0526785 5.86739 0 6 0Z" fill="white"></path></svg> Add Instruction
                                            </button>
                                        </div>
                                        {instructions.map((field, index) => (
                                            <div key={field.id} className="instruction-row mb-5">
                                                <InputField
                                                    type="text"
                                                    register={register(`instruction.${index}.step`, { required: 'Step is required' })}
                                                    className='mb-3'
                                                    label="Step"
                                                    name={`instruction.${index}.step`}
                                                    placeholder="Step"
                                                />
                                                <Textarea
                                                    className='mb-3'
                                                    register={register(`instruction.${index}.description`, { required: 'Description is required' })}
                                                    name={`instruction.${index}.description`}
                                                    label='Description'
                                                    placeholder="Description"
                                                />
                                                <div className='text-end'>
                                                    <button type="button" className='btn btn-danger' onClick={() => removeInstruction(index)}>Remove</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                </div>

                                <div className="mb-3 col-12">
                                    <SeoForm
                                        ref={seoFormRef}
                                        entityId={id || ''}
                                        entityType="category"
                                        initialData={seoData}
                                        isRequired={false}
                                    />
                                </div>
                            </div>
                            <div className="col-12 text-end mt-3  ">
                                <Button loading={loading} className="btn btn-primary text-white px-5" type="submit" text="Save" />
                            </div>
                        </form>
                    </div>
                </div>
                <div className='col-lg-4 col-12'>
                    {/* Images */}
                    <div className='app-card app-card-orders-table shadow-sm mb-4 p-4'>
                        <div className="mb-3 col-12 image-upload">
                            <div className='form-label'>Add recipe image</div>
                            <label htmlFor="file-input" className="choosefilenew position-relative">
                                <input
                                    type="file"
                                    id="file-input"
                                    accept="image/*"
                                    hidden
                                    multiple
                                    {...register('images', {
                                        validate: () => (imageURLs.length > 0 ? true : 'At least one image is required'),
                                    })}
                                    onChange={handleFileChange}
                                />
                                <div><div className='mb-2'><i className="bi fs-4 bi-upload"></i></div>Upload Images</div>
                            </label>
                            <div className='addimagefiles mt-2'>
                                {imageURLs.map((url, index) => (
                                    <div className="image-card" key={index}>
                                        <img src={url} alt={`Uploaded ${index}`} className="image-preview" />
                                        <button className='btn btn-danger px-2 py-1 text-white position-absolute' type="button" onClick={() => handleDeleteImage(url)}><i className='bi bi-trash3'></i></button>
                                    </div>
                                ))}
                            </div>
                            <ErrorMessage message={errors.images?.message?.toString()} />
                            <ErrorMessage message={error?.toString()} />
                        </div>
                    </div>
                </div>
                {/* Submit Button */}

            </div>
        </Layout>
    )
}

export default AddRecipe